import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import { normalizeThemeHexColor } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import beginPlayImg from "../img/begin.png";
import { fetchThemeData } from "../../admin/themeSlice";
import { listQuestions } from "../../admin/functions/questionsApi";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../admin/sessionSlice";
import {
  getFirstSetupPath,
} from "../gameSetupStorage";

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);

  const beginPlayButtonTextStyle = useMemo(() => {
    const textColor = normalizeThemeHexColor(
      themeData?.button_Textcolor ?? themeData?.button_text_color
    );
    if (!textColor) return undefined;
    return { color: textColor };
  }, [themeData]);

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;

      setBeginLoading(true);
      try {
        let themePayload = themeData;
        try {
          const freshTheme = await dispatch(
            fetchThemeData({ themeId: null })
          ).unwrap();
          themePayload = freshTheme?.data ?? themeData;
        } catch (fetchErr) {
          console.error("handleBeginPlay theme fetch:", fetchErr);
        }

        // `themename` is the theme's real slug/id (themesdata's key) — themeName is
        // only the human-readable display name, not what the questions table uses.
        const themeSlug = themePayload?.themename ?? themePayload?.themeName;
        const noOfQuestion = parseInt(themePayload?.noOfQuestion, 10) || 0;
        if (noOfQuestion > 0 && themeSlug && adminToken) {
          try {
            const availableQuestions = await listQuestions({
              themeName: themeSlug,
              organizationId: organizationId || "admin",
              sessionId: sessionId || "admin",
              token: adminToken,
            });
            if (availableQuestions.length < noOfQuestion) {
              await Swal.fire({
                icon: "warning",
                title: "Not enough questions",
                text: `This theme needs at least ${noOfQuestion} questions configured (currently ${availableQuestions.length}). Add more in Admin → Theme → Game Content.`,
                confirmButtonText: "OK",
              });
              return;
            }
          } catch (questionsErr) {
            console.error("handleBeginPlay questions fetch:", questionsErr);
          }
        }

        navigate(getFirstSetupPath(), { replace: true });
      } catch (err) {
        console.error("handleBeginPlay:", err);
        await Swal.fire({
          icon: "error",
          title: "Unable to start",
          text: err?.message || "Something went wrong. Please try again.",
          confirmButtonText: "OK",
        });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate, dispatch, themeData, adminToken, organizationId, sessionId]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    const body = document.body;
    const keys = [
      "background-attachment",
      "background-size",
      "background-repeat",
      "background-position",
      "overflow-x",
      "margin",
      "padding",
      "height",
      "font-family",
      "backdrop-filter",
    ];
    const snapshot = keys.map((k) => [k, body.style.getPropertyValue(k)]);

    body.style.setProperty("background-attachment", "fixed");
    body.style.setProperty("background-size", "100% 100%");
    body.style.setProperty("background-repeat", "no-repeat");
    body.style.setProperty("background-position", "center");
    body.style.setProperty("overflow-x", "hidden");
    body.style.setProperty("margin", "0");
    body.style.setProperty("padding", "0");
    body.style.setProperty("height", "100vh");
    body.style.setProperty(
      "font-family",
      "'FiraSans-Medium', sans-serif",
      "important"
    );
  

    return () => {
      snapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    Swal.fire("You have already played!", "  ", "error").then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="login-logo1"
          alt="Game Logo"
        />
      )}
        {user && status === "succeeded" && (
          <button
            type="button"
            className="begin-play-btn"
            disabled={beginLoading}
            onClick={handleBeginPlay}
          >
            {beginLoading ? (
              <span style={beginPlayButtonTextStyle}>LOADING…</span>
            ) : (
              <img src={beginPlayImg} alt="Begin Play" className="begin-play-img" />
            )}
          </button>
        )}
    </div>
  );
}

export default Login;
